import React from "react";
import { Plus, Trash2 } from "lucide-react";

const DEFAULT_AGG = {
  agg_side: "b", group_by_cols: [], agg_col: "",
  agg_function: "sum",
  join_key_pairs: [{ single_col: "", agg_col: "" }],
  value_col_single: "",
  tolerance_type: "absolute", tolerance_value: 0.01,
};

export default function AggregationBlock({ rule, index, onChange }) {
  const agg = rule.agg_config || DEFAULT_AGG;
  const setAgg = (patch) => onChange({ ...rule, agg_config: { ...agg, ...patch } });
  const updatePair = (ki, field, value) => {
    const pairs = [...(agg.join_key_pairs || [])];
    pairs[ki] = { ...pairs[ki], [field]: value };
    setAgg({ join_key_pairs: pairs });
  };
  const addPair = () => setAgg({ join_key_pairs: [...(agg.join_key_pairs || []), { single_col: "", agg_col: "" }] });
  const removePair = (ki) => setAgg({ join_key_pairs: (agg.join_key_pairs || []).filter((_, i) => i !== ki) });

  return (
    <div style={{ marginBottom: "1rem", padding: "0.75rem", background: "#F5F3FF", border: "1px solid #DDD6FE", borderRadius: "0.5rem" }}>
      <p style={{ fontSize: "0.78rem", fontWeight: 600, color: "#5B21B6", margin: "0 0 0.5rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>Aggregation Configuration</p>
      <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: "0 0 0.75rem", lineHeight: 1.4 }}>
        The "many" side groups rows by key and aggregates a column; the total must match a single row on the other side.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.5rem", marginBottom: "0.5rem" }}>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#5B21B6", display: "block", marginBottom: "0.2rem" }}>Aggregate Side (many)</label>
          <select data-testid={`agg-side-${index}`} className="recon-input" style={{ fontSize: "0.8rem", width: "100%" }} value={agg.agg_side || "b"} onChange={e => setAgg({ agg_side: e.target.value })}>
            <option value="b">File B (group B, match A)</option>
            <option value="a">File A (group A, match B)</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#5B21B6", display: "block", marginBottom: "0.2rem" }}>Function</label>
          <select data-testid={`agg-fn-${index}`} className="recon-input" style={{ fontSize: "0.8rem", width: "100%" }} value={agg.agg_function || "sum"} onChange={e => setAgg({ agg_function: e.target.value })}>
            <option value="sum">Sum</option>
            <option value="count">Count</option>
            <option value="avg">Avg</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#5B21B6", display: "block", marginBottom: "0.2rem" }}>Aggregate Column</label>
          <input data-testid={`agg-col-${index}`} className="recon-input" style={{ fontSize: "0.8rem", width: "100%", fontFamily: "JetBrains Mono, monospace" }} value={agg.agg_col || ""} onChange={e => setAgg({ agg_col: e.target.value })} placeholder="e.g. amount" />
        </div>
      </div>

      <div style={{ marginBottom: "0.5rem" }}>
        <label style={{ fontSize: "0.7rem", color: "#5B21B6", display: "block", marginBottom: "0.2rem" }}>Group By Columns (many-side, comma-separated)</label>
        <input data-testid={`agg-groupby-${index}`} className="recon-input" style={{ fontSize: "0.8rem", width: "100%", fontFamily: "JetBrains Mono, monospace" }} value={(agg.group_by_cols || []).join(", ")} onChange={e => setAgg({ group_by_cols: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} placeholder="e.g. employee_id, month" />
      </div>

      <div style={{ marginBottom: "0.5rem" }}>
        <label style={{ fontSize: "0.7rem", color: "#5B21B6", display: "block", marginBottom: "0.2rem" }}>Single-side value column (expected total)</label>
        <input data-testid={`agg-single-${index}`} className="recon-input" style={{ fontSize: "0.8rem", width: "100%", fontFamily: "JetBrains Mono, monospace" }} value={agg.value_col_single || ""} onChange={e => setAgg({ value_col_single: e.target.value })} placeholder="e.g. total_salary" />
      </div>

      <div style={{ marginBottom: "0.5rem" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <label style={{ fontSize: "0.7rem", color: "#5B21B6", marginBottom: "0.2rem", fontWeight: 600 }}>Join Keys (single → agg)</label>
          <button onClick={addPair} style={{ display: "flex", alignItems: "center", gap: "0.25rem", fontSize: "0.72rem", color: "#5B21B6", background: "none", border: "none", cursor: "pointer" }}><Plus size={10} /> Add</button>
        </div>
        {(agg.join_key_pairs || []).map((jp, ki) => (
          <div key={ki} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "0.375rem", marginBottom: "0.25rem" }}>
            <input data-testid={`agg-jp-single-${index}-${ki}`} className="recon-input" value={jp.single_col || ""} onChange={e => updatePair(ki, "single_col", e.target.value)} placeholder="single-side col" style={{ fontSize: "0.78rem", fontFamily: "JetBrains Mono, monospace" }} />
            <input data-testid={`agg-jp-agg-${index}-${ki}`} className="recon-input" value={jp.agg_col || ""} onChange={e => updatePair(ki, "agg_col", e.target.value)} placeholder="agg-side col" style={{ fontSize: "0.78rem", fontFamily: "JetBrains Mono, monospace" }} />
            <button onClick={() => removePair(ki)} style={{ background: "none", border: "none", cursor: "pointer", color: "#DC2626" }}><Trash2 size={11} /></button>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem" }}>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#5B21B6", display: "block", marginBottom: "0.2rem" }}>Tolerance Type</label>
          <select data-testid={`agg-toltype-${index}`} className="recon-input" style={{ fontSize: "0.8rem", width: "100%" }} value={agg.tolerance_type || "absolute"} onChange={e => setAgg({ tolerance_type: e.target.value })}>
            <option value="absolute">Absolute</option>
            <option value="percentage">Percentage</option>
          </select>
        </div>
        <div>
          <label style={{ fontSize: "0.7rem", color: "#5B21B6", display: "block", marginBottom: "0.2rem" }}>Tolerance Value</label>
          <input data-testid={`agg-tolval-${index}`} type="number" step="0.01" className="recon-input" style={{ fontSize: "0.8rem", width: "100%" }} value={agg.tolerance_value ?? 0.01} onChange={e => setAgg({ tolerance_value: parseFloat(e.target.value) || 0 })} />
        </div>
      </div>
    </div>
  );
}
